// AddDatabaseModalFooter.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './AddDatabaseModalFooter.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import { LEGACY_LIBRARY_UI_ENABLED } from 'legacy/legacyFeatureFlags';

/**
 * AddDatabaseModalFooter
 *
 * Renders the step-specific footer actions for the add-database wizard.
 *
 * @param {object} flowState - Modal flow state that determines which actions are visible.
 * @param {object} libraryState - Template provisioning state and handlers.
 * @param {object} createTypeState - Create-type form state and handlers.
 * @param {object} modalActions - General modal navigation and close handlers.
 *
 * @returns {ReactNode} The rendered footer actions.
 */
const AddDatabaseModalFooter = ({
    flowState,
    libraryState,
    createTypeState,
    modalActions,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    if (flowState.isLandingStep) {
        return null;
    }

    // ----- RENDER ----- //

    return (
        <ModalButtons>

            {/* BACK NAVIGATION — only when landing/template flow exists */}
            {LEGACY_LIBRARY_UI_ENABLED && (flowState.isTemplateStep || flowState.isCreateStep) ? (
                <div className="add-database-modal-back">
                    <Button
                      backgroundColor="transparent"
                      hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                      iconKey="arrow_back"
                      label="Back"
                      onClick={
                            flowState.isTemplateStep
                                ? libraryState.handleTemplateBack
                                : modalActions.returnToLanding
                        }
                    />
                </div>
            ) : null}

            {LEGACY_LIBRARY_UI_ENABLED && flowState.isTemplateStep && libraryState.isAddonPreviewStep ? (
                <Button
                  iconKey="check"
                  enabled={!libraryState.isProvisioning}
                  label={libraryState.isProvisioning ? 'Provisioning...' : 'Add to Space'}
                  onClick={libraryState.handleUseSelectedAddon}
                />
            ) : null}

            {flowState.isCreateFlow && !flowState.hasCreatedType ? (
                <Button
                  label={createTypeState.isSavingType ? 'Creating...' : 'Create Database'}
                  enabled={createTypeState.canSaveType}
                  onClick={createTypeState.handleCreateType}
                />
            ) : null}

            {flowState.isCreateStep && flowState.hasCreatedType && (
                <>
                    <Button
                      label="Done"
                      onClick={modalActions.handleClose}
                    />
                    <Button
                      iconKey="check"
                      label={createTypeState.isUpdatingType ? 'Saving...' : 'Save Changes'}
                      enabled={createTypeState.canSaveCreatedType}
                      onClick={createTypeState.handleSaveCreatedType}
                    />
                </>
            )}

            {flowState.isFieldsStep ? (
                <Button
                  label="Done"
                  onClick={modalActions.handleClose}
                />
            ) : null}

        </ModalButtons>
    );
};

// Dev Tools
AddDatabaseModalFooter.displayName = 'AddDatabaseModalFooter';

export default AddDatabaseModalFooter;
